import { useEffect } from 'react';
import useStyles from './styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { NotifyOptions } from 'lib/errors';

/**
 * Props for the LoadingError component.
 */
export type LoadingErrorPropTypes = {
  /** The name of the resource that failed to load (e.g., 'post', 'featured post banner') */
  failedTarget: string;
  /** The error object that occurred */
  error: unknown;
  /** Function to report the error (typically to an error tracking service) */
  reportError: (err: unknown, options?: NotifyOptions) => void;
};

/**
 * LoadingError Component
 *
 * Displays a user-friendly error message when content fails to load.
 * Automatically reports the error to the error tracking service (e.g., Bugsnag)
 * with metadata about what failed to load.
 *
 * Features:
 * - Shows a clear error message to users
 * - Automatically reports errors in the background
 * - Includes metadata about the failed target
 *
 * @param {LoadingErrorPropTypes} props - Component props
 * @returns {JSX.Element} Error message and guidance for the user
 */
const LoadingError = (props: LoadingErrorPropTypes) => {
  const { classes } = useStyles();
  const { failedTarget, error, reportError } = props;

  useEffect(() => {
    // Report error in the background.
    reportError(error, {
      metaData: {
        errorThrownOn: failedTarget,
      },
    });
  }, [error, failedTarget, reportError]);

  return (
    <Box className={classes.root} data-component="LoadingError">
      <Typography variant="subtitle2" color="textSecondary">
        Error loading {failedTarget}. Please reload the page and try again.
      </Typography>
    </Box>
  );
};

export default LoadingError;
